import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY_PRINCIPLE = { title: "", description: "" };

export default function AdminAbout() {
  const { user, loading } = useAuth();
  const [form, setForm] = useState({
    quote: "",
    bio_paragraphs: [""],
    principles: [{ ...EMPTY_PRINCIPLE }],
    twitter: "",
    email: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/site/about").then((r) => {
      const d = r.data || {};
      setForm({
        quote: d.quote || "",
        bio_paragraphs: d.bio_paragraphs?.length ? d.bio_paragraphs : [""],
        principles: d.principles?.length ? d.principles : [{ ...EMPTY_PRINCIPLE }],
        twitter: d.twitter || "",
        email: d.email || "",
      });
    });
  }, []);

  if (loading) return <div className="container-editorial py-24 text-center">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const setPara = (i, v) => setForm((f) => ({ ...f, bio_paragraphs: f.bio_paragraphs.map((p, ix) => ix === i ? v : p) }));
  const addPara = () => setForm((f) => ({ ...f, bio_paragraphs: [...f.bio_paragraphs, ""] }));
  const removePara = (i) => setForm((f) => ({ ...f, bio_paragraphs: f.bio_paragraphs.filter((_, ix) => ix !== i) }));

  const setPrinc = (i, k, v) => setForm((f) => ({ ...f, principles: f.principles.map((p, ix) => ix === i ? { ...p, [k]: v } : p) }));
  const addPrinc = () => setForm((f) => ({ ...f, principles: [...f.principles, { ...EMPTY_PRINCIPLE }] }));
  const removePrinc = (i) => setForm((f) => ({ ...f, principles: f.principles.filter((_, ix) => ix !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        quote: form.quote,
        bio_paragraphs: form.bio_paragraphs.filter((p) => p.trim()),
        principles: form.principles.filter((p) => p.title.trim() && p.description.trim()),
        twitter: form.twitter,
        email: form.email,
      };
      await api.put("/site/about", payload);
      toast.success("About page saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container-editorial py-12">
      <Link to="/admin" className="kicker text-[color:var(--text-muted)] flex items-center gap-2 hover:text-[color:var(--accent)]"><ArrowLeft size={14} /> Back to editor desk</Link>
      <h1 className="font-serif text-5xl font-bold mt-4 mb-2 tracking-tight">Edit About page</h1>
      <p className="text-[color:var(--text-muted)] mb-8">Wrap words in <code>**double asterisks**</code> to make them bold.</p>

      <form onSubmit={submit} className="grid md:grid-cols-3 gap-8" data-testid="about-editor-form">
        <div className="md:col-span-2 space-y-6">
          <label className="block">
            <span className="kicker block mb-2">Pull quote</span>
            <textarea rows={2} value={form.quote} onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))} className="w-full bg-transparent border px-4 py-3 outline-none font-serif italic" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="about-quote" />
          </label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="kicker">Bio paragraphs</span>
              <button type="button" onClick={addPara} className="text-xs kicker text-[color:var(--accent)] hover-underline inline-flex items-center gap-1" data-testid="about-add-para"><Plus size={12} /> Add paragraph</button>
            </div>
            <div className="space-y-3">
              {form.bio_paragraphs.map((p, i) => (
                <div key={i} className="flex gap-2 items-start" data-testid={`about-para-row-${i}`}>
                  <textarea rows={4} value={p} onChange={(e) => setPara(i, e.target.value)} className="flex-1 bg-transparent border px-4 py-3 outline-none" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid={`about-para-${i}`} />
                  {form.bio_paragraphs.length > 1 && (
                    <button type="button" onClick={() => removePara(i)} className="p-2 text-[color:var(--accent)] hover:bg-[color:var(--muted)]" data-testid={`about-remove-para-${i}`}><Trash2 size={16} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="kicker">Principles (title · description)</span>
              <button type="button" onClick={addPrinc} className="text-xs kicker text-[color:var(--accent)] hover-underline inline-flex items-center gap-1" data-testid="about-add-princ"><Plus size={12} /> Add principle</button>
            </div>
            <div className="space-y-3">
              {form.principles.map((p, i) => (
                <div key={i} className="border p-3 space-y-2" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid={`about-princ-row-${i}`}>
                  <input value={p.title} onChange={(e) => setPrinc(i, "title", e.target.value)} placeholder="Title (e.g. Slow)" className="w-full bg-transparent border px-3 py-2 outline-none font-serif text-xl font-bold" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid={`about-princ-title-${i}`} />
                  <textarea rows={2} value={p.description} onChange={(e) => setPrinc(i, "description", e.target.value)} placeholder="One-line description" className="w-full bg-transparent border px-3 py-2 outline-none" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid={`about-princ-desc-${i}`} />
                  {form.principles.length > 1 && (
                    <button type="button" onClick={() => removePrinc(i)} className="text-xs kicker text-[color:var(--accent)] inline-flex items-center gap-1" data-testid={`about-remove-princ-${i}`}><Trash2 size={12} /> Remove</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="space-y-5">
          <div className="border p-4 bg-[color:var(--surface)]" style={{ borderColor: "hsl(var(--border-hsl))" }}>
            <button disabled={busy} className="btn-accent w-full flex items-center justify-center gap-2 py-4" data-testid="about-save">
              <Save size={16} /> {busy ? "Saving…" : "Save About page"}
            </button>
          </div>

          <label className="block">
            <span className="kicker block mb-2">Twitter handle</span>
            <input value={form.twitter} onChange={(e) => setForm((f) => ({ ...f, twitter: e.target.value }))} placeholder="@juliamfosah" className="w-full bg-transparent border px-3 py-2 outline-none text-sm" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="about-twitter" />
          </label>
          <label className="block">
            <span className="kicker block mb-2">Contact email</span>
            <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="editor@ittvnews.com" className="w-full bg-transparent border px-3 py-2 outline-none text-sm" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="about-email" />
          </label>
          <div className="text-xs text-[color:var(--text-muted)] leading-relaxed">
            The portrait image is edited in the article editor via the &ldquo;Upload image&rdquo; button — set on any story you author and it becomes your byline avatar everywhere.
          </div>
        </aside>
      </form>
    </div>
  );
}
